import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard } from 'data/shared/CreditCard';
import {
  usePromotionalTypes,
  PromotionalTypesState,
} from 'data/shared/promotionalTypes';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';
import {
  shortToPromoType,
  promoTypeToShort,
} from 'data/shared/promoShortCodes';

const DELIMITER = '.';
const PARAM_KEY = 'p';

export const useURLParamPromotionalTypes = (cards: CreditCard[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial state from URL
  const paramValue = searchParams.get(PARAM_KEY);
  const initialTypes: PromotionalType[] = paramValue
    ? paramValue.split(DELIMITER).map(shortToPromoType)
    : [];

  const [overrides, setOverridesInternal] =
    useState<PromotionalTypesState>(initialTypes);

  // Wrap setOverrides to also update URL
  const setOverrides = useCallback(
    (
      action: PromotionalTypesState | ((prev: PromotionalTypesState) => PromotionalTypesState)
    ) => {
      setOverridesInternal((prev) => {
        const newTypes = typeof action === 'function' ? action(prev) : action;
        return newTypes;
      });
    },
    []
  );

  // Sync state to URL
  useEffect(() => {
    const encoded = overrides.map(promoTypeToShort).join(DELIMITER);

    setSearchParams(
      (prev) => {
        if (encoded) {
          prev.set(PARAM_KEY, encoded);
        } else {
          prev.delete(PARAM_KEY);
        }
        return prev;
      },
      { replace: true }
    );
  }, [overrides, setSearchParams]);

  return usePromotionalTypes(setOverrides, overrides);
};
